if (!customElements.get('media-gallery-new')) {
  customElements.define(
    'media-gallery-new',
    class MediaGalleryNew extends HTMLElement {
      constructor() {
        super();
        this.slider = null;
      }

      connectedCallback() {
        this.list = this.querySelector('ul.keen-slider');
        this.bar = this.querySelector('.card-slider-bar__inner');
        this.dots = this.querySelector('.product-gallery__dots');
        this.thumbnails = Array.from(this.querySelectorAll('.product-gallery-thumbnail'));
        this.prevButton = this.querySelector('[data-gallery-prev]');
        this.nextButton = this.querySelector('[data-gallery-next]');
        this.customBadges = this.querySelector('[data-custom-badges]');

        if (!this.list) return;

        this.initSlider();
        this.initThumbnails();
        this.initArrows();

        if (this.dataset.initialFilterValue) {
          this.applyVariantAltFilter(this.dataset.initialFilterValue);
        }

        this.observer = new MutationObserver(() => {
          if (this.slider) {
            this.slider.update();
            this.updateBar(this.slider);
          }
        });
        this.observer.observe(this.list, { childList: true });
      }

      initSlider() {
        if (typeof KeenSlider === 'undefined') {
             // Assume script is loading
             setTimeout(() => this.initSlider(), 100);
             return;
        }

        if (this.slider) return;

        this.slider = new KeenSlider(this.list, {
          selector: '.keen-slider__slide:not([hidden])',
          loop: false,
          slides: {
            perView: 1,
            spacing: 0,
          },
          dragSpeed: 1,
          initial: 0,
          defaultAnimation: {
            duration: 0,
          },
          created: (s) => {
            this.classList.add('slider-initialized');
            this.updateBar(s);
            this.updateDots(s);
            this.updateArrows(s);
            this.updateThumbnailsFromSlider(s);
            this.updateCustomBadges(s);
          },
          detailsChanged: (s) => {
            this.updateBar(s);
            this.updateDots(s);
            this.updateArrows(s);
            this.updateCustomBadges(s);
          },
          slideChanged: (s) => {
            this.updateBar(s);
            this.updateDots(s);
            this.updateArrows(s);
            this.updateThumbnailsFromSlider(s);
            this.updateCustomBadges(s);
          },
        });
      }

      initArrows() {
        if (!this.prevButton && !this.nextButton) return;

        if (this.prevButton) {
          this.prevButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.movePrev();
          });
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.moveNext();
          });
        }
      }

      movePrev() {
        if (!this.slider) {
          setTimeout(() => this.movePrev(), 100);
          return;
        }
        const rel = this.slider.track?.details?.rel ?? 0;
        this.slider.moveToIdx(Math.max(0, rel - 1), true, { duration: 0 });
      }

      moveNext() {
        if (!this.slider) {
          setTimeout(() => this.moveNext(), 100);
          return;
        }
        const details = this.slider.track?.details;
        const rel = details?.rel ?? 0;
        const max = Math.max(0, (details?.slides?.length ?? 1) - 1);
        this.slider.moveToIdx(Math.min(max, rel + 1), true, { duration: 0 });
      }

      updateDots(s) {
        if (!this.dots || !s || !s.track || !s.track.details) return;

        const count = s.track.details.slides.length;
        if (count <= 1) {
          this.dots.style.display = 'none';
          return;
        }

        this.dots.style.display = 'flex';

        const activeIndex = s.track.details.rel;
        const existingDots = this.dots.querySelectorAll('.product-upsell__dot');
        if (existingDots.length !== count) {
          this.dots.innerHTML = '';
          s.track.details.slides.forEach((_slide, idx) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.classList.add('product-upsell__dot');
            if (idx === activeIndex) dot.classList.add('active');
            dot.addEventListener('click', () => s.moveToIdx(idx));
            this.dots.appendChild(dot);
          });
          return;
        }

        existingDots.forEach((dot, idx) => {
          dot.classList.toggle('active', idx === activeIndex);
        });
      }
      initThumbnails() {
        if (!this.thumbnails || !this.thumbnails.length) return;

        this.thumbnails.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const target = button.dataset.target;
            if (!target) return;
            this.setActiveMedia(target);
            this.setActiveThumbnail(target);
          });
        });
      }

      updateBar(s) {
        if (!this.bar || !s || !s.track || !s.track.details) return;

        const slides = s.track.details.slides.length;

        if (this.bar.parentElement) {
          if (slides <= 1) {
            this.bar.parentElement.style.display = 'none';
          } else {
            this.bar.parentElement.style.display = 'block';
          }
        }

        const width = 100 / slides;
        this.bar.style.width = `${width}%`;

        const progress = Math.max(0, Math.min(1, s.track.details.progress));
        const slideIndex = progress * (slides - 1);
        this.bar.style.transform = `translateX(${slideIndex * 100}%)`;
      }

      updateArrows(s) {
        if ((!this.prevButton && !this.nextButton) || !s || !s.track || !s.track.details) return;

        const count = s.track.details.slides.length;
        const rel = s.track.details.rel;
        const shouldShow = count > 1;

        if (this.prevButton) {
          this.prevButton.style.display = shouldShow ? '' : 'none';
          this.prevButton.disabled = !shouldShow || rel <= 0;
        }
        if (this.nextButton) {
          this.nextButton.style.display = shouldShow ? '' : 'none';
          this.nextButton.disabled = !shouldShow || rel >= count - 1;
        }
      }

      updateThumbnailsFromSlider(s) {
        if (!this.thumbnails || !this.thumbnails.length || !s || !s.track || !s.track.details) return;

        const index = s.track.details.rel;
        const slides = Array.from(this.list.querySelectorAll('.keen-slider__slide:not([hidden])'));
        const current = slides[index];
        if (!current) return;
        const currentId = current.dataset.mediaId;
        if (!currentId) return;

        this.setActiveThumbnail(currentId);
      }

      updateCustomBadges(s) {
        if (!this.customBadges || !s || !s.track || !s.track.details) return;
        const index = s.track.details.rel ?? 0;
        this.customBadges.hidden = index !== 0;
      }

      setActiveThumbnail(mediaId) {
        if (!this.thumbnails || !this.thumbnails.length) return;

        this.thumbnails.forEach((button) => {
          if (button.hidden) {
            button.classList.remove('is-active');
            return;
          }
          const isActive = button.dataset.target === mediaId;
          button.classList.toggle('is-active', isActive);
        });
      }

      setActiveMedia(mediaId, prepend) {
        // If the slider isn't ready, wait
        if (!this.slider) {
            setTimeout(() => this.setActiveMedia(mediaId, prepend), 100);
            return;
        }
        
        // Find index of mediaId
        // The list children might have changed, so get fresh list
        const slides = Array.from(this.list.querySelectorAll('.keen-slider__slide:not([hidden])'));
        const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        
        if (index !== -1) {
            this.slider.moveToIdx(index);
        }
      }

      normalizeFilterValue(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
      }

      getNormalizedMediaAlt(element) {
        const datasetAlt = element?.dataset?.mediaAlt;
        const imageAlt = element?.querySelector?.('img')?.getAttribute('alt');
        return this.normalizeFilterValue(datasetAlt != null ? datasetAlt : imageAlt);
      }

      shouldAlwaysShowMedia(element) {
        const altValue = this.getNormalizedMediaAlt(element);
        if (!altValue) return true;

        const productTitle = this.normalizeFilterValue(this.dataset.productTitle);
        return productTitle ? altValue.includes(productTitle) : false;
      }

      rebuildSlider(activeMediaId) {
        if (this.slider) {
          this.slider.destroy();
          this.slider = null;
        }

        this.classList.remove('slider-initialized');
        this.initSlider();

        const visibleSlides = Array.from(this.list.querySelectorAll('.keen-slider__slide:not([hidden])'));
        const nextActiveMediaId =
          activeMediaId && visibleSlides.some((slide) => slide.dataset.mediaId === activeMediaId)
            ? activeMediaId
            : visibleSlides[0]?.dataset?.mediaId;

        if (nextActiveMediaId) {
          this.setActiveThumbnail(nextActiveMediaId);
          setTimeout(() => this.setActiveMedia(nextActiveMediaId), 0);
        }
      }

      applyVariantAltFilter(rawValue) {
        if (!this.list) return;

        const filterValue = this.normalizeFilterValue(rawValue);
        const slides = Array.from(this.list.querySelectorAll('.keen-slider__slide'));
        const thumbs = Array.from(this.querySelectorAll('.product-gallery-thumbnail'));
        if (!slides.length) return;

        const visibleSlides = slides.filter((slide) => {
          if (!filterValue) return true;
          if (this.shouldAlwaysShowMedia(slide)) return true;
          const altValue = this.getNormalizedMediaAlt(slide);
          return altValue.includes(filterValue);
        });

        const visibleMediaIds = visibleSlides.map((slide) => slide.dataset.mediaId);

        const visibleMediaSet = new Set(visibleMediaIds);
        slides.forEach((slide) => {
          slide.hidden = !visibleMediaSet.has(slide.dataset.mediaId);
          slide.classList.toggle('is-active', !slide.hidden && visibleMediaIds[0] === slide.dataset.mediaId);
        });

        thumbs.forEach((thumb) => {
          if (!filterValue) {
            thumb.hidden = false;
            return;
          }
          if (this.shouldAlwaysShowMedia(thumb)) {
            thumb.hidden = false;
            return;
          }
          const thumbAlt = this.getNormalizedMediaAlt(thumb);
          thumb.hidden = !thumbAlt.includes(filterValue);
        });

        this.dataset.currentFilterValue = rawValue || '';
        this.rebuildSlider(visibleMediaIds[0]);
      }

      disconnectedCallback() {
        if (this.slider) this.slider.destroy();
        if (this.observer) this.observer.disconnect();
      }
    }
  );
}
